import React, {
  memo,
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  HTMLProps,
  NamedExoticComponent,
  useCallback,
} from 'react'
import { render } from 'react-dom'
import Flatpickr from 'react-flatpickr'
import classNames from 'classnames'
import get from 'lodash/get'
import uuid from 'uuid'
import { CalendarIcon } from 'transfix-icons'
import { FORMATS } from 'constants/datetime'
import { FLATPICKR_DEFAULTS } from 'constants/formElements'
import { Buttons } from './Buttons'
import 'flatpickr/dist/themes/airbnb.css'
import 'scss/5_components/_new-date-picker.scss'

interface RangeDatePickerProps extends HTMLProps<HTMLInputElement> {
  id?: string
  dates?: Date[]
  validationMessage?: string
  invalid?: boolean
  onDateChange?: (dates: Date[]) => void
  onClick?: () => void
  large?: boolean
  mode?: string
  maxDate?: Date
  minDate?: Date
  dateFormat?: string
  datePickerOptions?: object
  placeholder?: string
  label?: string
  showApplyButton?: boolean
}

export const RangeDatePicker: NamedExoticComponent<RangeDatePickerProps> = memo(
  ({
    className,
    dates = [],
    disabled = false,
    onDateChange,
    maxDate,
    minDate,
    dateFormat = FORMATS.singleDateFormat,
    invalid = false,
    required,
    validationMessage,
    large,
    placeholder = 'Pick a date range',
    label = '',
    id = uuid(),
    showApplyButton = false,
  }) => {
    const datePickerOptions = { maxDate, minDate, dateFormat }
    const showMonthsRange = 2
    const calendarRef = useRef(null)
    const [selectedDates, setSelectedDates] = useState(dates)

    const onChangeHandler = (newDates: Date[]) => {
      setSelectedDates(newDates)
    }

    useEffect(() => {
      if (onDateChange) {
        onDateChange(selectedDates)
      }
    }, [selectedDates])

    const applyHandler = useCallback(() => {
      const close = get(calendarRef.current, 'flatpickr.close') as (() => void) | undefined

      if (close) close()
    }, [])

    const clearHandler = useCallback(() => {
      setSelectedDates([])
    }, [setSelectedDates])

    useLayoutEffect(() => {
      const flatpickrContainer = get(calendarRef.current, 'flatpickr.calendarContainer') as
        | HTMLElement
        | undefined

      if (!flatpickrContainer) return () => {}

      const buttonsContainer = document.createElement('div')

      flatpickrContainer.appendChild(buttonsContainer)
      render(
        <Buttons onApply={applyHandler} onClear={clearHandler} showApplyButton={showApplyButton} />,
        buttonsContainer
      )

      return () => {
        setTimeout(() => {
          flatpickrContainer.removeChild(buttonsContainer)
        })
      }
    }, [showApplyButton, applyHandler, clearHandler])

    const validationText = validationMessage || (required ? 'Required' : '')
    const classes = classNames('date-picker-container', className, {
      invalid,
      'date-picker-large': large,
    })

    return (
      <div className={classes}>
        {label && (
          <label
            htmlFor={id}
            id={`label-${id}`}
            className={`label ${disabled ? 'disabled' : ''} ${required ? 'required' : ''}`}>
            {label}
          </label>
        )}
        <div className='date-picker-container'>
          <CalendarIcon className='date-picker-icon' color='skyBlue6' />
          <Flatpickr
            id={id}
            ref={calendarRef}
            value={selectedDates}
            placeholder={placeholder}
            disabled={disabled}
            className='date-picker-range'
            options={{
              ...FLATPICKR_DEFAULTS,
              ...datePickerOptions,
              mode: 'range',
              showMonths: showMonthsRange,
            }}
            onChange={onChangeHandler}
          />
        </div>
        {validationText && <span className='validation-message'>{validationText}</span>}
      </div>
    )
  }
)

export default RangeDatePicker
